import type { ChangeEvent } from 'react';
import React from 'react';
import type { FormBlurEventFn } from '@noths/polaris-client-react-hooks';
import { useSingleExecutionFunction } from '@noths/polaris-client-react-hooks';
import { Feedback } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import type { RuleSet } from 'styled-components';

import { RadioButton } from 'src/components/atoms/RadioButton/RadioButton';
import * as groupStyles from 'src/components/molecules/styles/Group.styles';
import { useTracking } from 'src/hooks/useTracking/useTracking';

export type RadioButtonData<TValue extends string | boolean = string | boolean> = {
  labelText: string;
  value: TValue;
};

export interface RadioButtonGroupProps<TValue extends string | boolean> {
  accessibleLabel: string;
  additionalCss?: RuleSet<object>;
  'data-testid'?: string;
  description?: string;
  error?: string | null;
  hideLabel?: boolean;
  name: string;
  onBlur?: FormBlurEventFn;
  onChange: (value: TValue, name: string) => void;
  radioButtonData: RadioButtonData<TValue>[];
  required?: boolean;
  value: TValue | undefined;
}

export const RadioButtonGroup = <TValue extends string | boolean>({
  accessibleLabel,
  additionalCss,
  'data-testid': dataGroupTestId,
  description,
  error,
  hideLabel = false,
  name,
  onBlur,
  onChange,
  radioButtonData,
  required = false,
  value,
  ...additionalProps
}: RadioButtonGroupProps<TValue>) => {
  const { trackCompleted } = useTracking();
  const trackCompletedOnce = useSingleExecutionFunction((event: ChangeEvent<HTMLInputElement>) =>
    trackCompleted(event),
  );

  return (
    <fieldset
      css={[groupStyles.borderless, additionalCss]}
      data-testid={dataGroupTestId}
      onBlur={onBlur}
    >
      <legend css={hideLabel ? visuallyHidden : undefined} id={`${name}-legend`}>
        {accessibleLabel}
      </legend>
      {description && <p id={`${name}-description`}>{description}</p>}
      {radioButtonData.map(({ labelText, value: radioButtonValue }) => (
        <RadioButton
          checked={radioButtonValue === value}
          invalid={!!error}
          key={radioButtonValue as string}
          labelText={labelText}
          name={name}
          onChange={(event) => {
            trackCompletedOnce(event);
            onChange(radioButtonValue, name);
          }}
          required={required}
          value={radioButtonValue as string}
          {...additionalProps}
        />
      ))}
      {error && (
        <div css={groupStyles.fieldFeedbackContainer} role="alert">
          <Feedback error iconAlign="left" id={`${name}-error`} name={error} />
        </div>
      )}
    </fieldset>
  );
};
